/**
 * The agent-hub-wide half of every agent's Run form: the providers and the
 * project variables that hold their keys. Nothing here is specific to one
 * agent.
 */

/**
 * The `MODEL` env value. Each agent resolves a provider alias to its own tested
 * model, so Console never names a concrete model id. Agent releases older than
 * the aliases reject these values at startup.
 */
export type Provider = "anthropic" | "openai";

export const DEFAULT_PROVIDER: Provider = "anthropic";

/**
 * Which provider a form opens on, given the keys the project has.
 *
 * The agent needs the key that matches its `MODEL`, so opening on Anthropic for
 * a project holding only `OPENAI_API_KEY` disables Run over a key already set.
 * Anthropic wins whenever its key is usable, including before the deployment
 * answers — both flags are then false.
 */
export const resolveDefaultProvider = (
  hasAnthropicKey: boolean,
  hasOpenAIKey: boolean
): Provider => (!hasAnthropicKey && hasOpenAIKey ? "openai" : DEFAULT_PROVIDER);

/** Provider → the project variable the customer must set (never a form input). */
export const PROVIDER_KEY_ENV: Record<Provider, string> = {
  anthropic: "ANTHROPIC_API_KEY",
  openai: "OPENAI_API_KEY"
};

export const PROVIDER_LABELS: Record<Provider, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI"
};

/** Whether the selected provider's key was found on the environment. */
export type ApiKeyStatus = "present" | "absent" | "unknown";
